import type { SettingDep, Trilingual } from './types.ts';
import { CARD_SPECS } from './cards.generated.ts';

/**
 * 卡片结构：一页上每张卡片的每一行，**包括不是设置项的那些**。
 *
 * ════════════════════════════════════════════════════════
 * 为什么要有这一层
 * ════════════════════════════════════════════════════════
 * 设置项清单（SETTING_DEFS）只回答「有哪些值能存」。可一张卡片上还有：
 *   说明（note）     「标记完成会立刻停止催办」这种要让人知道的话
 *   小标题（sect）    把一张大卡分成几段
 *   按钮（btns）      「发送测试推送」「补录这笔交易」
 *   定制块（custom）  统计图、列表、二维码……由专门的界面画
 *   折叠组（fold）    默认收起来的一组设置
 *
 * 以前生成时这些全被扔掉了，网页版于是画不出来 —— 更糟的是**没法如实说它们还没做**：
 * 一张卡片上的按钮和统计图凭空消失，用户看到的是一张「看起来完整」的卡片。
 * 这个产品里「看起来完整但少了一块」比「明说少了一块」危险得多。
 *
 * 纯模块：只引类型和生成的数据，浏览器能直接用。
 */

export interface CardBtn {
  label: Trilingual | null;
  /** 原型里的动作名（有的按钮没有） */
  act: string | null;
  /** 主按钮 */
  primary: boolean;
}

export type CardRow =
  | { t: 'key'; key: string }
  | { t: 'note'; text: Trilingual | null; dep: SettingDep | null }
  | { t: 'sect'; text: Trilingual | null; dep: SettingDep | null }
  | { t: 'btns'; btns: readonly CardBtn[]; dep: SettingDep | null }
  | { t: 'custom'; name: string; dep: SettingDep | null }
  | { t: 'fold'; label: Trilingual | null; name: string | null; rows: readonly CardRow[] };

export interface CardSpec {
  /** 板块 id。有编号用「页面:编号」，没有用中文卡名 —— 和网页上可拖板块的 id 一致 */
  id: string;
  page: string;
  no: number | null;
  title: Trilingual;
  sub: Trilingual | null;
  rows: readonly CardRow[];
}

export { CARD_SPECS };

/** 某一页的全部卡片，顺序照原型 */
export function cardsOfPage(page: string): readonly CardSpec[] {
  return CARD_SPECS[page] ?? [];
}

/** 把卡片里的行（含折叠组里的）逐个交给 `fn` */
export type FoldRow = Extract<CardRow, { t: 'fold' }>;

export function walkRows(rows: readonly CardRow[], fn: (row: CardRow, fold: FoldRow | null) => void): void {
  for (const r of rows) {
    fn(r, null);
    if (r.t === 'fold') for (const x of r.rows) fn(x, r);
  }
}

/** 一张卡片上的全部设置项键（含折叠组里的） */
export function keysOfCard(card: CardSpec): string[] {
  const out: string[] = [];
  walkRows(card.rows, r => { if (r.t === 'key') out.push(r.key); });
  return out;
}
